import {
  useActivePlaylistDescriptors,
  useEstimatedPlaylistDuration,
  useIsPlaylistCompleted,
  useIsPlaylistInProgressForUser,
  useIsRecommendedPlaylist,
  usePlaylistById,
  usePlaylistTags,
} from "@/store/selectors/playlistSelectors";
import {
  Badge,
  Button,
  Flex,
  Group,
  lighten,
  Space,
  Stack,
} from "@mantine/core";
import { Text } from "../ui/Text";
import {
  IconArrowRight,
  IconCheck,
  IconClock,
  IconStar,
  IconStarFilled,
} from "@tabler/icons-react";
import { useIsTabletOrBigger, useStyles } from "@/styles/useStyles";
import { PlaylistAdvizerList } from "./PlaylistAdvizerList";
import { Navigation } from "@/services/Navigation";
import { StoreService } from "@/services/StoreService";
import { Analytics } from "@/services/Analytics";
import { useCallback, useMemo, useState } from "react";
import { AdvizerTag } from "../AdvizerTag";
import { useGoToNextStepInPlaylist } from "@/store/selectors/playlistSelectors";
import { PartnerLogo } from "../PartnerLogo";
import { useActiveUserPartner } from "@/store/selectors";

export function PlaylistCard({
  playlistId,
  isPlaylistIntro,
  maxAdvizerListHeight,
  advizerQuoteLineClamp,
  maxWidth,
  height,
  isPlaylistConclusion,
  isPlaylistLibrary,
  isPreview,
  startButtonOnly,
  advizerListHeight,
  hideRecommendedBanner,
  renderWhenPlaylistCompleted,
}: {
  playlistId: string;
  isPlaylistIntro?: boolean;
  maxAdvizerListHeight?: number;
  maxSpoilerHeight?: number;
  advizerQuoteLineClamp?: number;
  maxWidth?: number;
  height?: number;
  isPlaylistConclusion?: boolean;
  isPlaylistLibrary?: boolean;
  isPreview?: boolean;
  startButtonOnly?: boolean;
  advizerListHeight?: number;
  renderWhenPlaylistCompleted?: () => React.ReactNode;
  bg?: string;
  hideRecommendedBanner?: boolean;
}) {
  const playlist = usePlaylistById(playlistId);
  const { theme } = useStyles();
  const [, setIsHovered] = useState(false);
  const isPlaylistInProgressForUser =
    useIsPlaylistInProgressForUser(playlistId);
  const isRecommendedPlaylist = useIsRecommendedPlaylist(playlistId);
  const playlistDescriptors = useActivePlaylistDescriptors(playlistId);
  const playlistTags = usePlaylistTags(playlistId);
  const isTabletOrBigger = useIsTabletOrBigger();
  const goToNextStepInPlaylist = useGoToNextStepInPlaylist();
  const isPlaylistCompleted = useIsPlaylistCompleted(playlistId);
  const estimatedPlaylistDuration = useEstimatedPlaylistDuration(playlistId);
  const activeUserPartner = useActiveUserPartner();
  const handleStartPlaylist = useCallback(() => {
    const action = isPlaylistInProgressForUser ? "continue" : "start";

    if (!isPlaylistIntro) {
      StoreService.clearActivePlaylist();
    }

    if (isPlaylistIntro && isPlaylistCompleted) {
      console.log("1");
      goToNextStepInPlaylist();
    } else {
      console.log("2");
      goToNextStepInPlaylist();

      Navigation.navToPlaylistAndAutoNavToNextStep({ playlistId });
      // goToNextStepInPlaylist();
    }

    Analytics.playlistStart({
      playlist,
      context: isPlaylistConclusion
        ? "playlist_conclusion"
        : isPlaylistIntro
          ? "playlist_intro"
          : "playlist_library",
      action,
    });
  }, [
    isPlaylistCompleted,
    goToNextStepInPlaylist,
    isPlaylistConclusion,
    isPlaylistInProgressForUser,
    isPlaylistIntro,
    playlist,
    playlistId,
  ]);

  const playlistUniqId = useMemo(() => {
    return playlist?.config?.uniqId || playlist?.uniqId;
  }, [playlist]);

  const PlaylistTags = useMemo(() => {
    if (isPreview) return null;
    return (
      <Group
        gap={10}
        align="center"
        justify={isPlaylistIntro ? "flex-start" : "flex-start"}
        w="100%"
      >
        {isPlaylistCompleted && (
          <>
            <Badge
              color="green"
              size="md"
              leftSection={<IconCheck size={16} />}
              style={{ textTransform: "none" }}
            >
              Completed
            </Badge>
          </>
        )}
        {estimatedPlaylistDuration && (
          <Badge
            color="primary"
            variant="filled"
            leftSection={<IconClock size={12} />}
            style={{ textTransform: "none" }}
          >
            {estimatedPlaylistDuration} min
          </Badge>
        )}

        {isRecommendedPlaylist && (
          <>
            <Badge
              color="primary"
              size="md"
              leftSection={<IconStar size={12} />}
              style={{ textTransform: "none" }}
            >
              Recommended
            </Badge>
          </>
        )}
        {playlistTags.map((tag) => (
          <AdvizerTag key={tag.id} tag={tag} disableClick={true} maw={250} />
        ))}
      </Group>
    );
  }, [
    estimatedPlaylistDuration,
    isPlaylistCompleted,
    isPlaylistIntro,
    isPreview,
    isRecommendedPlaylist,
    playlistTags,
  ]);

  const playlistPartnerSlug = useMemo(() => {
    return playlistUniqId?.partners?.[0];
  }, [playlistUniqId]);

  const StartButton = useMemo(() => {
    if (isPlaylistCompleted && renderWhenPlaylistCompleted) {
      return renderWhenPlaylistCompleted();
    }
    return (
      <Button
        variant={
          // startButtonOnly
          //   ? "transparent"
          //   :
          isPlaylistCompleted
            ? "outline"
            : isPlaylistInProgressForUser
              ? "filled"
              : "light"
        }
        color="primary"
        size={"sm"}
        radius="xl"
        rightSection={<IconArrowRight size={18} />}
        onClick={handleStartPlaylist}
        miw="fit-content"
      >
        {isPlaylistCompleted
          ? "View"
          : isPlaylistInProgressForUser
            ? "Continue"
            : "Start"}
      </Button>
    );
  }, [
    handleStartPlaylist,
    isPlaylistCompleted,
    isPlaylistInProgressForUser,
    renderWhenPlaylistCompleted,
  ]);

  const PlaylistTitle = useMemo(() => {
    return (
      <Group
        w="100%"
        justify="space-between"
        wrap="nowrap"
        align={playlistPartnerSlug ? "center" : "center"}
        gap={0}
      >
        {playlistPartnerSlug && (
          <Flex
            w={isPlaylistIntro ? 64 : 54}
            align="center"
            justify="flex-start"
            mr="xs"
          >
            <PartnerLogo
              // withTitle
              width={isPlaylistIntro ? 50 : 44}
              partnerSlug={playlistPartnerSlug}
            />
          </Flex>
        )}
        <a
          title={playlist?.title}
          aria-label={playlist?.title}
          onClick={handleStartPlaylist}
          style={{ flex: 1 }}
        >
          <Text
            titleStyle={isPlaylistIntro ? "h2" : "h3"}
            lh={1.1}
            ta={isPlaylistIntro ? "left" : "left"}
            c={playlistPartnerSlug ? "primary.9" : "text.9"}
            flex={1}
            lineClamp={isTabletOrBigger && isPlaylistLibrary ? 2 : 3}
          >
            {playlist?.title}
          </Text>
        </a>
        {isTabletOrBigger && isPlaylistLibrary && StartButton}
      </Group>
    );
  }, [
    playlistPartnerSlug,
    playlist?.title,
    handleStartPlaylist,
    isPlaylistIntro,
    isTabletOrBigger,
    isPlaylistLibrary,
    StartButton,
  ]);

  const PlalistDescriptors = useMemo(() => {
    if (!playlistDescriptors?.length) return null;
    return (
      <Group>
        <Text
          c="text.7"
          lh={1.3}
          ta="left"
          titleStyle="h6"
          fz={16}
          fw={400}
          lineClamp={
            isPlaylistIntro || isPreview ? 5 : isTabletOrBigger ? 2 : 3
          }
        >
          <span style={{ fontWeight: 600 }}>Topics: </span>
          {playlistDescriptors.map((d) => d.text).join(", ")}
        </Text>
      </Group>
    );
  }, [isPlaylistIntro, isPreview, isTabletOrBigger, playlistDescriptors]);

  if (!playlist) return null;

  if (startButtonOnly) {
    return StartButton;
  }

  return (
    <Group
      mx="auto"
      p={isPreview ? 0 : "xl"}
      bg={
        isPreview
          ? "transparent"
          : isPlaylistCompleted
            ? "background.2"
            : playlistPartnerSlug
              ? lighten(theme.colors.primary[0], 0.5)
              : "background.0"
      }
      h={"100%"}
      w="100%"
      maw={maxWidth}
      mah={height}
      align={isTabletOrBigger ? "center" : "flex-start"}
      wrap="nowrap"
      pos="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      classNames={{
        root:
          isPlaylistInProgressForUser && !playlistPartnerSlug
            ? "pulseBorder"
            : "",
      }}
      style={{
        borderRadius: 8,
        // cursor: "pointer",
        border: isPreview
          ? "none"
          : `${playlistPartnerSlug ? 0 : 1}px solid ${isPlaylistCompleted ? theme.colors.border[3] : playlistPartnerSlug ? theme.colors.secondary[9] : theme.colors.border[3]}`,
        // borderTop: playlistPartnerSlug
        //   ? `8px solid ${theme.colors.secondary[9]}`
        //   : undefined,
        boxShadow:
          isPlaylistCompleted || isPreview
            ? undefined
            : playlistPartnerSlug
              ? "0 0 8px 0 rgba(0, 0, 0, 0.2)"
              : "0 0 3px 0 rgba(0, 0, 0, 0.1)",
      }}
      // className="shadow-on-hover"
    >
      <Stack gap={0} w="100%" justify="space-between" h="100%">
        <Stack
          gap={0}
          w="100%"
          h="100%"
          flex={1}
          pt={
            hideRecommendedBanner
              ? undefined
              : playlistPartnerSlug || isRecommendedPlaylist
                ? playlistPartnerSlug && isRecommendedPlaylist
                  ? 84
                  : "xl"
                : undefined
          }
          style={{ overflow: "auto" }}
        >
          {playlistPartnerSlug && (
            <Group
              w="100%"
              bg={theme.colors.primary[9]}
              pos="absolute"
              top={0}
              left={0}
              right={0}
              align="center"
              justify="center"
              py={6}
              pt={8}
              style={{
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                zIndex: 2,
              }}
            >
              <IconStarFilled size={20} color={theme.colors.secondary[9]} />
              <Text titleStyle="h5" c="secondary.9" fw={700} lh={1} mt={2}>
                {activeUserPartner?.slug === "adobe"
                  ? `Featuring Adobe Certified Professionals`
                  : `Featuring ${activeUserPartner?.title} alumni`}
              </Text>
              <IconStarFilled size={20} color={theme.colors.secondary[9]} />
            </Group>
          )}
          {isRecommendedPlaylist && !hideRecommendedBanner && (
            <Group
              w="100%"
              bg={theme.colors.primary[9]}
              pos="absolute"
              top={playlistPartnerSlug ? 42 : 0}
              left={0}
              right={0}
              align="center"
              justify="center"
              py={6}
              pt={8}
              style={{
                borderTopLeftRadius: playlistPartnerSlug ? 0 : 8,
                borderTopRightRadius: playlistPartnerSlug ? 0 : 8,
                zIndex: 1,
              }}
            >
              <IconStarFilled
                size={playlistPartnerSlug ? 18 : 20}
                color={theme.colors.secondary[7]}
              />
              <Text
                titleStyle={playlistPartnerSlug ? "h6" : "h5"}
                c="secondary.7"
                fw={700}
                lh={1}
                mt={2}
              >
                Recommended for You
              </Text>
              <IconStarFilled
                size={playlistPartnerSlug ? 18 : 20}
                color={theme.colors.secondary[7]}
              />
            </Group>
          )}
          {PlaylistTitle}
          <Space h={isTabletOrBigger ? "md" : "sm"} />

          <Stack gap={0} pr="xl">
            {PlalistDescriptors}

            {/* {(!isTabletOrBigger || !isPlaylistLibrary) && ( */}
            <>
              <Space h="md" />
              {PlaylistTags}
            </>
            {/* )} */}
            {isPreview ? null : (
              <>
                <Space h={isTabletOrBigger ? "lg" : "xl"} />

                <PlaylistAdvizerList
                  playlistId={playlistId}
                  maxAdvizerListHeight={maxAdvizerListHeight}
                  advizerQuoteLineClamp={advizerQuoteLineClamp}
                  advizerListHeight={advizerListHeight}
                />
              </>
            )}
          </Stack>
        </Stack>

        {
          !isPreview && (!isTabletOrBigger || !isPlaylistLibrary) ? (
            <Stack gap={0} h={80} justify="flex-end">
              <Button
                variant={
                  isPlaylistCompleted
                    ? "outline"
                    : isPlaylistIntro || isPlaylistInProgressForUser
                      ? "filled"
                      : "light"
                }
                color="primary"
                size="md"
                radius="xl"
                fullWidth
                rightSection={<IconArrowRight size={18} />}
                mih={40}
                onClick={handleStartPlaylist}
              >
                {isPlaylistCompleted
                  ? "View"
                  : isPlaylistInProgressForUser
                    ? "Continue"
                    : "Start"}{" "}
                Playlist
              </Button>
              {/* {StartButton} */}
            </Stack>
          ) : null
          // PlaylistTags
        }
      </Stack>
      {/* {isLaptopOrBigger && (
        <Stack w={40} align="flex-end">
          <IconArrowRight size={24} />
        </Stack>
      )} */}
    </Group>
  );
}
